import _get from 'lodash/get';
import { TranslateFunction } from 'react-localize-redux';
import { hashHistory } from 'react-router';

import {
  FulfilmentStatus,
  OrderHistory,
  OrderHistoryItems,
  OrderState,
  Reorder,
  ReorderMenuItem,
} from '../core/api/order/order.types';
import {
  CollectionRestaurantDetail,
  DeliveryRestaurantDetail,
  DeliveryType,
} from '../core/api/restaurant/restaurant.types';
import { analyticsService } from '../core/services/analyticsService/analyticsService';
import { Basket, BasketItem } from '../core/state/order/order.types';
import { AsyncMap } from '../core/types/data.types';
import { OrderType } from '../core/types/route.types';
import * as Api from '../weborder/Api';
import { basketUtils } from '../weborder/redux/basket/basketUtils';
import { setBasket as setBasketLocalStorage } from '../weborder/services/storageServices';
import {
  ORDER_STATUS_FIRST_STEP_POLL_DELAY,
  ORDER_STATUS_POLL_DELAY,
} from './constants';
import {
  dateTimeFormats,
  formatDateTimeString,
  getDateFromMsDate,
  msToIanaTimezoneName,
  utcToZoned,
} from './dateTimeUtils';
import { deliveryKey } from './makeUtils';
import { urlUtils } from './urlUtils';

export const getOrderStatusPollDelay = (
  orderStatus: FulfilmentStatus | undefined,
): number => {
  if (orderStatus && !orderStatus.IsError) {
    if (orderStatus.StatusPosition === 0) {
      return ORDER_STATUS_FIRST_STEP_POLL_DELAY;
    }
    if (orderStatus.StatusPosition < orderStatus.TotalStatuses) {
      return ORDER_STATUS_POLL_DELAY;
    }
  }
  return null;
};

export const getDeliveryOrderDisplayTime = (
  deliveryTime: string,
  order: Api.PreviousOrderDm | OrderHistory,
  restaurantList: AsyncMap<DeliveryRestaurantDetail>,
  isError?: boolean,
): string => {
  const deliveryRestaurantKey = deliveryKey(
    order.VirtualRestaurantId,
    order.DeliveryLocationId,
  );
  const restaurant = _get(restaurantList, deliveryRestaurantKey);
  let orderTime: string;

  if (restaurant && restaurant.status === 'success') {
    orderTime = getOrderTime(order, deliveryTime, restaurant.data, isError);
  }

  return orderTime;
};

export const getCollectionOrderDisplayTime = (
  collectionTime: string,
  restaurantList: AsyncMap<CollectionRestaurantDetail>,
  order: Api.PreviousOrderDm | OrderHistory,
  isError?: boolean,
): string => {
  const restaurant = _get(restaurantList, order.PhysicalRestaurantId);
  let orderTime: string;

  if (restaurant && restaurant.status === 'success') {
    orderTime = getOrderTime(order, collectionTime, restaurant.data, isError);
  }

  return orderTime;
};

const getOrderTime = (
  order: Api.PreviousOrderDm | OrderHistory,
  orderTime: string,
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  isError?: boolean,
): string => {
  const timeZone = msToIanaTimezoneName(restaurant.TimeZoneInfoId);
  const displayTimeString = isError
    ? order.TsOrderPlaced.toString()
    : orderTime;
  const displayTimeDate = getDateFromMsDate(displayTimeString);
  const utcTimeToTimeZone = utcToZoned(displayTimeDate, timeZone);

  return formatDateTimeString(
    utcTimeToTimeZone,
    dateTimeFormats.standardDateTime,
  );
};

export const getOrderCollectionOrDeliveryTime = (
  order: Api.PreviousOrderDm | OrderHistory,
  orderStatus: FulfilmentStatus,
  collectionRestaurants: AsyncMap<CollectionRestaurantDetail>,
  deliveryRestaurants: AsyncMap<DeliveryRestaurantDetail>,
  translate: TranslateFunction,
): string => {
  if (orderStatus?.StatusPosition === 0 && !orderStatus?.IsError) {
    return translate('OrderComplete_Pending') as string;
  }

  if (order && (orderStatus?.EstimatedCompletionTime || orderStatus?.IsError)) {
    const time =
      order.DeliveryType === DeliveryType.Delivery
        ? getDeliveryOrderDisplayTime(
            orderStatus?.EstimatedCompletionTime,
            order,
            deliveryRestaurants,
            orderStatus?.IsError,
          )
        : getCollectionOrderDisplayTime(
            orderStatus?.EstimatedCompletionTime,
            collectionRestaurants,
            order,
            orderStatus?.IsError,
          );
    return time;
  }

  return undefined;
};

export const getOrderStatusViewedEventData = (
  fulfilmentStatus: FulfilmentStatus,
  orderId: number,
): {
  amp_label: string;
  amp_orderStatus: string;
  amp_orderId: number;
  amp_statusPosition: number;
  amp_totalStatuses: number;
} => {
  const {
    StatusName: statusName,
    StatusPosition: statusPosition,
    TotalStatuses: totalStatuses,
  } = fulfilmentStatus;

  return {
    amp_label: 'Order Status Viewed',
    amp_orderStatus: statusName,
    amp_orderId: orderId,
    amp_statusPosition: statusPosition,
    amp_totalStatuses: totalStatuses,
  };
};

// TODO - remove as part of ORD-866
export const getFilteredOrderHistory = (
  orderHistory: OrderHistory[],
  fulfilmentType: DeliveryType,
  count: number,
  physicalRestaurantId?: number,
): OrderHistory[] => {
  let filteredOrderHistory = orderHistory.filter(
    (prevOrder) => prevOrder.DeliveryType === fulfilmentType,
  );

  if (physicalRestaurantId) {
    filteredOrderHistory = filteredOrderHistory?.filter(
      (filteredOrder) =>
        filteredOrder.PhysicalRestaurantId === physicalRestaurantId,
    );
  }

  return filteredOrderHistory?.length
    ? filteredOrderHistory.slice(0, count)
    : [];
};

// TODO - remove as part of ORD-866
export const getCheckoutUrlFromOrderHistory = (
  order: OrderHistory,
  deliveryLocationId?: string,
): string => {
  const concessionStoreId =
    order.OrderHistoryItems[0].ConcessionStore?.ConcessionStoreId;
  const concessionStoreIdPath = concessionStoreId
    ? `/${concessionStoreId}`
    : '';
  const isDeliveryOrder = order.DeliveryType === 0;
  const type = isDeliveryOrder ? 'delivery' : 'collection';
  const id = isDeliveryOrder ? deliveryLocationId : order.PhysicalRestaurantId;

  return `/checkout/${order.VirtualRestaurantId}/${type}/${id}${concessionStoreIdPath}`;
};

export const getCheckoutUrlFromReorder = (
  order: Reorder,
  deliveryLocationId?: string,
): string => {
  const concessionStoreId = order.MenuItems[0].ConcessionStoreId;
  const concessionStoreIdPath = concessionStoreId
    ? `/${concessionStoreId}`
    : '';
  const isDeliveryOrder = order.Fulfilment.Type === DeliveryType.Delivery;
  const type = isDeliveryOrder ? OrderType.DELIVERY : OrderType.COLLECTION;
  const id = isDeliveryOrder ? deliveryLocationId : order.Store.Id;

  return `/checkout/${order.Store.StoreGroupId}/${type}/${id}${concessionStoreIdPath}`;
};

// TODO - remove as part of ORD-866
export const getBasketItemsFromOrderHistory = (
  orderHistoryItems: OrderHistoryItems[],
): { [uniqueKey: string]: BasketItem } => {
  const basketItems: { [uniqueKey: string]: BasketItem } = {};

  orderHistoryItems.forEach((orderItem) => {
    const orderOptionSets =
      orderItem.OrderHistoryItemOptions?.map((x) => x.MenuItemOptionId) || [];

    const basketItem: BasketItem = {
      count: 1,
      ConcessionStoreId: orderItem.ConcessionStore?.ConcessionStoreId,
      itemId: orderItem.MenuItemId,
      options: orderOptionSets,
      order: undefined,
    };

    const key = basketUtils.uniqueItemId(basketItem);

    if (basketItems[key]) {
      basketItems[key].count += 1;
    } else {
      basketItems[key] = basketItem;
    }
  });

  return basketItems;
};

export const getAvailableBasketItemsFromReorder = (
  reorderItems: ReorderMenuItem[],
): { [uniqueKey: string]: BasketItem } => {
  const basketItems: { [uniqueKey: string]: BasketItem } = {};

  reorderItems.forEach((reorderItem) => {
    if (!reorderItem.Available) {
      return;
    }

    const orderOptionSets = reorderItem.OptionSets?.map((x) => x.Id) || [];

    const basketItem: BasketItem = {
      count: 1,
      ConcessionStoreId: reorderItem.ConcessionStoreId,
      itemId: reorderItem.Id,
      options: orderOptionSets,
      order: undefined,
    };

    const key = basketUtils.uniqueItemId(basketItem);

    if (basketItems[key]) {
      basketItems[key].count += 1;
    } else {
      basketItems[key] = basketItem;
    }
  });

  return basketItems;
};

export const getUnvailableBasketItemNamesFromReorder = (
  reorderItems: ReorderMenuItem[],
): string[] => {
  const unavailableItems: string[] = [];

  reorderItems.forEach((reorderItem) => {
    if (!reorderItem.Available) {
      unavailableItems.push(reorderItem.Name);
    }
  });

  return unavailableItems;
};

// TODO - remove as part of ORD-866
export const getUnvailableOrderItemIds = (
  menuSections: any[],
  orderItems: OrderHistoryItems[],
): {
  orderItemId: number;
  orderItemName: string;
}[] => {
  const unavailableOrderItemIds: {
    orderItemId: number;
    orderItemName: string;
  }[] = [];
  const availableMenuItems: number[] = [];
  const availableMenuItemOptions: number[] = [];

  menuSections?.forEach((menuSection) =>
    // get available menu items
    menuSection.MenuItems?.forEach((menuItem) => {
      if (menuItem.IsAvailable && !menuItem.IsDeleted) {
        availableMenuItems.push(menuItem.MenuItemId);
      }

      // get available menu item options
      menuItem.MenuItemOptionSets?.forEach((menuItemOptionSet) => {
        menuItemOptionSet.MenuItemOptionSetItems?.forEach(
          (menuItemOptionSetItem) => {
            if (
              menuItemOptionSetItem.IsAvailable &&
              !menuItemOptionSetItem.IsDeleted
            ) {
              availableMenuItemOptions.push(
                menuItemOptionSetItem.MenuItemOptionSetItemId,
              );
            }
          },
        );
      });
    }),
  );

  // check order items vs available menu items and item options
  orderItems?.forEach((orderItem) => {
    const orderOptionItems =
      orderItem.OrderHistoryItemOptions?.map(
        (orderHistoryItemOption) => orderHistoryItemOption.MenuItemOptionId,
      ) || [];

    const orderMenuItemIsUnavailable = !availableMenuItems.includes(
      orderItem.MenuItemId,
    );
    const orderMenuItemOptionIsUnavailable = !orderOptionItems.every(
      (orderOptionItem) => availableMenuItemOptions.includes(orderOptionItem),
    );

    if (orderMenuItemIsUnavailable || orderMenuItemOptionIsUnavailable) {
      const unavailableOrderItem = {
        orderItemId: orderItem.MenuItemId,
        orderItemName: orderItem.Name,
      };
      unavailableOrderItemIds.push(unavailableOrderItem);
    }
  });

  return unavailableOrderItemIds;
};

// TODO - remove as part of ORD-866
export const reorder = (
  orderDetails: OrderHistory,
  setBasket: (payload: Basket) => void,
  setReorderOrderId: (orderId: number) => void,
  source: 'Store list' | 'Menu' | 'Order history' | 'Website widget',
  deliveryLocationId?: string,
): void => {
  const checkoutUrl = getCheckoutUrlFromOrderHistory(
    orderDetails,
    deliveryLocationId || orderDetails.DeliveryLocationId?.toString(),
  );
  const basketItems = getBasketItemsFromOrderHistory(
    orderDetails.OrderHistoryItems,
  );
  const menuId = orderDetails.OrderHistoryItems[0].MenuId;
  const eventData = {
    amp_label: 'Reorder used',
    amp_orderId: orderDetails.OrderId,
    amp_source: source,
  };

  setReorderOrderId(orderDetails.OrderId);

  setBasket({
    createdOrders: {},
    items: basketItems,
    menuId,
    status: 'loaded',
    validationErrors: [],
  });

  setBasketLocalStorage(menuId, {
    menuId,
    items: basketItems,
  });

  analyticsService.track(eventData);

  urlUtils.navigateToHash(checkoutUrl);
};

export const newReorder = (
  orderDetails: Reorder,
  setBasket: (payload: Basket) => void,
  setUnavailableReorderItems: (items: string[]) => void,
  source: 'Store list' | 'Menu' | 'Order history' | 'Website widget',
  deliveryLocationId?: string,
  isRedirected = false,
): void => {
  const checkoutUrl = getCheckoutUrlFromReorder(
    orderDetails,
    deliveryLocationId ||
      orderDetails.Fulfilment.DeliveryLocationId?.toString(),
  );
  const availableBasketItems = getAvailableBasketItemsFromReorder(
    orderDetails.MenuItems,
  );
  const unavailableOrderItemNames = getUnvailableBasketItemNamesFromReorder(
    orderDetails.MenuItems,
  );

  const menuId = orderDetails.MenuItems[0].MenuId;
  const eventData = {
    amp_label: 'Reorder used',
    amp_orderId: orderDetails.OrderId,
    amp_source: source,
  };

  !!unavailableOrderItemNames.length &&
    setUnavailableReorderItems(unavailableOrderItemNames);

  setBasket({
    createdOrders: {},
    items: availableBasketItems,
    menuId,
    status: 'loaded',
    validationErrors: [],
  });

  setBasketLocalStorage(menuId, {
    menuId,
    items: availableBasketItems,
  });

  analyticsService.track(eventData);

  if (isRedirected) {
    // This replaces the URL wihout adding a new entry to the browser history.
    hashHistory.replace(checkoutUrl);
  } else {
    urlUtils.navigateToHash(checkoutUrl);
  }
};

export const checkIsOrderCancelled = (orderState: OrderState): boolean => {
  const ORDER_CANCELLED_STATES = [
    OrderState.Cancelled,
    OrderState.RejectedByRestaurant,
    OrderState.RejectedByFlipdish,
    OrderState.RejectedAutomatically,
    OrderState.RejectedAfterBeingAccepted,
  ];

  return ORDER_CANCELLED_STATES.includes(orderState);
};
